/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { colors, fonts } from '@blend/consumer-components';
import { Orientations, mediaBreakpoints, mediaOrientations } from '../../../common/responsive';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { DocumentsIcon } from './icons/DocumentsIcon';
import { ParticipantsIcon } from './icons/ParticipantsIcon';
import { SettingsIcon } from './icons/SettingsIcon';
import { ActivityLogIcon } from './icons/ActivityLogIcon';
import { ExitIcon } from './icons/ExitIcon';
import { Icon } from './icons/Icon';
import { SigningRoomNavigationItemIds } from './signingRoomItems';

export namespace SigningRoomNavigationItem {
  export interface Props {
    id: SigningRoomNavigationItemIds;
    title: string;
    contentComponent: any;
    count?: number;
    isSelected?: boolean;
    onClick: (navigationItemId: string) => void;
  }
  export type RenderProps = Omit<SigningRoomNavigationItem.Props, 'contentComponent'>;
}

export const selectedWrapperClassName = 'selectedWrapper';

const mobileIconSize = 24;
const desktopIconSize = 30;

const styles = StyleSheet.create({
  wrapper: {
    background: 'none',
    border: 'none',
    [mediaBreakpoints.desktop]: {
      color: colors.white,
      width: 48,
      height: 48,
      padding: 9,
    },
    [mediaBreakpoints.mobile]: {
      color: colors.catalystLight80,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      [mediaOrientations.portrait]: {
        width: 96,
      },
      [mediaOrientations.landscape]: {
        height: 36,
        marginBottom: 40,
        [`:last-child`]: {
          marginBottom: 0,
        },
      },
    },
  },
  [selectedWrapperClassName]: {
    [mediaBreakpoints.desktop]: {
      backgroundColor: colors.reflex,
    },
  },
  iconWrapper: {
    width: desktopIconSize,
    height: desktopIconSize,
    [mediaBreakpoints.mobile]: {
      width: mobileIconSize,
      height: mobileIconSize,
    },
  },
  label: {
    fontSize: 12,
    height: 12,
    fontWeight: 'normal',
    display: 'block',
    ...fonts.UntitledSans,
    [mediaBreakpoints.desktop]: {
      display: 'none',
    },
  },
  title: {
    [mediaOrientations.landscape]: {
      display: 'none',
    },
  },
  titleCountWrapper: {
    [mediaOrientations.landscape]: {
      display: 'none',
    },
  },
  selectedLabel: {
    color: colors.reflex,
  },
});

const getIcon: (id: string) => React.FC<Icon.Props> = (id: string) => {
  switch (id) {
    case 'settings':
      return SettingsIcon;
    case 'participants':
      return ParticipantsIcon;
    case 'activityLog':
      return ActivityLogIcon;
    case 'exit':
      return ExitIcon;
    case 'documents':
    default:
      return DocumentsIcon;
  }
};

const getIconColor = (isSelected: boolean, isMobileView: boolean) => {
  if (isMobileView) {
    return isSelected ? colors.reflex : colors.catalystLight80;
  } else {
    return colors.white;
  }
};

export const SigningRoomNavigationItem: React.FC<SigningRoomNavigationItem.RenderProps> = ({
  id,
  title,
  count,
  isSelected = false,
  onClick,
}) => {
  const { isMobileView, orientation } = useRootSelector(state => state.layout);
  const isPortrait = orientation === Orientations.portrait;
  const htmlId = `SigningRoomNavigationItem-${id}`;
  const IconComponent = getIcon(id);
  const hasCount = count !== undefined;
  // We have to render a space in the event that the title and count
  // are both rendered, or React will smash them together
  const titleCountDelimiter = ' ';

  return (
    <button
      type="button"
      data-it={htmlId}
      onClick={() => onClick(id)}
      id={htmlId}
      className={css(styles.wrapper, isSelected && styles[selectedWrapperClassName])}
    >
      <div className={css(styles.iconWrapper)}>
        <IconComponent
          fill={getIconColor(isSelected, isMobileView)}
          size={isMobileView ? mobileIconSize : desktopIconSize}
        />
      </div>
      <label htmlFor={htmlId} data-it="label" className={css(styles.label, isSelected && styles.selectedLabel)}>
        {isPortrait && (
          <span className={css(styles.title)} data-it="title">
            {title}
          </span>
        )}
        {isPortrait && hasCount && titleCountDelimiter}
        {hasCount && <span data-it="count">({count})</span>}
      </label>
    </button>
  );
};
